import { useEffect, useRef } from 'react';
import { useChat } from '../../hooks/useChat';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';

export function ChatPanel() {
  const { isOpen, minimize } = useChat();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when panel opens; return focus to launcher on minimize is
  // handled in ChatLauncher via its own effect.
  useEffect(() => {
    if (isOpen) {
      // Small delay so the transition finishes before grabbing focus
      const t = setTimeout(() => inputRef.current?.focus(), 180);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Escape key minimizes the panel
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') minimize();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, minimize]);

  return (
    <div
      role="dialog"
      aria-label="Chat with Aria"
      aria-modal="false"
      className={[
        // Base layout
        'fixed flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl',
        // Mobile: near-fullscreen from bottom
        'bottom-0 left-0 right-0 h-[calc(100dvh-48px)]',
        // Desktop: fixed-size panel anchored above launcher
        'sm:left-auto sm:right-6 sm:bottom-[88px] sm:w-[380px] sm:h-[560px] sm:rounded-2xl',
        // z-index
        'z-50',
        // Open/close transition
        'transition-all duration-200 ease-out',
        isOpen
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none',
      ].join(' ')}
    >
      <ChatHeader />
      <MessageList />
      <ChatInput inputRef={inputRef} />
    </div>
  );
}
